// ** react imports
import React, { memo, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// ** react router imports
import { useParams } from 'react-router-dom';

// ** react hook form imports
import { useWatch } from 'react-hook-form';

// ** components imports
import Badge from '../../../common/Badge';
import Loading from '../../../common/Loading';

// ** enums imports
import { BADGE_THEMES } from '../../../../enums/common';

// ** context imports
import { useQuiz } from '../../../../contexts/QuizContext';

// ** custom hooks
import { useAddTaxFileHistory } from '../../../../hooks/apis/useTaxFile';

const QuestionHeader = props => {
  // props
  const { question, hasError: hasErrorProp } = props;
  const { errors, control, isEditMode } = useQuiz();

  // hooks
  const { taxFileId } = useParams();

  // api hooks
  const { addTaxFileHistory, isAddTaxFileHistoryLoading } =
    useAddTaxFileHistory();

  // Local UI state for copy feedback
  const [copyStatus, setCopyStatus] = useState(''); // '', 'copied', 'empty', 'error'
  const [copiedCount, setCopiedCount] = useState(0);
  const hideTimer = useRef(null);

  const watchedValue = useWatch({
    control,
    name: question?.questionId || '__noop__',
  });

  const isRequired = useMemo(
    () =>
      Boolean(
        question?.required ??
          question?.isRequired ??
          question?.metadata?.required
      ),
    [question]
  );

  const isSecure = useMemo(
    () =>
      Boolean(
        question?.secure ?? question?.isSecure ?? question?.metadata?.secure
      ),
    [question]
  );

  const effectiveValue =
    watchedValue !== undefined ? watchedValue : question?.answer;

  const isUnanswered = useMemo(() => {
    if (!isRequired || question?.optedOut) return false;
    const v = effectiveValue;
    if (v == null) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'string') return v.trim().length === 0;
    return false;
  }, [effectiveValue, isRequired, question?.optedOut]);

  const hasError = hasErrorProp ?? Boolean(errors?.[question?.questionId]);

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const headerRef = useRef(null);

  // Extract values from elements-with-title inside this question container
  const collectTitledValues = rootEl => {
    // Prefer walking from the click target, fallback to headerRef
    const container =
      (rootEl && rootEl.closest && rootEl.closest('[data-question-id]')) ||
      headerRef.current?.closest('[data-question-id]') ||
      headerRef.current?.parentElement?.closest?.('[data-question-id]');
    if (!container) return [];
    // Broaden selection: collect from typical form controls within this question
    const nodes = container.querySelectorAll(
      'input:not([type="hidden"]), textarea, select, [contenteditable="true"]'
    );
    const values = [];
    nodes.forEach(el => {
      const tag = el.tagName.toLowerCase();
      if (tag === 'input') {
        const name = el.getAttribute('name');
        const type = (el.getAttribute('type') || 'text').toLowerCase();
        if (type === 'checkbox') {
          if (el.checked) values.push(el.value || 'on');
        } else if (type === 'radio') {
          if (el.checked) values.push(el.value || 'on');
        } else if (type === 'file') {
          // Browsers block reading file paths; skip silently
        } else {
          if (el.value != null && String(el.value).trim() !== '') {
            values.push(el.value);
          }
        }
      } else if (tag === 'textarea') {
        if (el.value != null && String(el.value).trim() !== '') {
          values.push(el.value);
        }
      } else if (tag === 'select') {
        const sel = /** @type {HTMLSelectElement} */ (el);
        if (sel.multiple) {
          const selected = Array.from(sel.selectedOptions)
            .map(o => o.value)
            .filter(Boolean);
          if (selected.length) values.push(selected.join(', '));
        } else if (sel.value != null && String(sel.value).trim() !== '') {
          values.push(sel.value);
        }
      } else if (el.getAttribute('contenteditable') === 'true') {
        const txt = (el.innerText || el.textContent || '').trim();
        if (txt) values.push(txt);
      }
    });
    return values;
  };

  // Clipboard write with fallback
  const writeToClipboard = async text => {
    console.log(text);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      // fall through to legacy path
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (err) {
      return false;
    }
  };

  const handleCopyAll = async e => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setCopyStatus('');
    setCopiedCount(0);

    let values = collectTitledValues(e?.currentTarget || headerRef.current);
    // Fallback: use the current field value if no DOM values found
    if (!values.length) {
      const v = effectiveValue;
      let text = '';
      if (Array.isArray(v)) {
        text = v.join(', ');
      } else if (v && typeof v === 'object') {
        try {
          text = JSON.stringify(v);
        } catch {
          text = '';
        }
      } else if (v != null) {
        text = String(v);
      }
      if (text.trim()) values = [text];
    }
    if (!values.length) {
      setCopyStatus('empty');
      hideTimer.current = setTimeout(() => setCopyStatus(''), 2000);
      return;
    }

    const payload = values.join('\n');
    const ok = await writeToClipboard(payload);
    if (ok) {
      setCopiedCount(values.length);
      setCopyStatus('copied');

      // Add tax file history entry for the copy action
      if (taxFileId) {
        await addTaxFileHistory(taxFileId, {
          questionId: question.questionId,
          questionText: question.questionText,
          copiedValueCount: values.length,
          copiedValues: values,
        });
      }
    } else {
      setCopyStatus('error');
    }
    hideTimer.current = setTimeout(() => setCopyStatus(''), 2200);
  };

  return (
    <div
      ref={headerRef}
      className="flex flex-wrap items-center justify-between gap-2 mb-2"
    >
      <div className="flex items-center gap-2">
        <p className="text-gray-80 dark:text-gray-200 text-sm font-medium">
          Q. {question.questionText}{' '}
          {isRequired && <span className="text-red-500 text-sm">*</span>}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {/* Error indicator */}
        {hasError && (
          <Badge theme={BADGE_THEMES.error} text="Error" size="xs" />
        )}

        {/* Enhanced Status Indicators */}
        {(question.unanswered || isUnanswered) &&
          isRequired &&
          !question.optedOut &&
          !hasError && (
            <Badge theme={BADGE_THEMES.required} text="Required" size="xs" />
          )}

        {!question.isValid &&
          question.validationErrors &&
          question.validationErrors.length > 0 &&
          !hasError && (
            <Badge theme={BADGE_THEMES.invalid} text="Invalid" size="xs" />
          )}

        {question.adminModified && (
          <Badge
            theme={BADGE_THEMES.admin_modified}
            text="Admin Modified"
            size="xs"
          />
        )}

        {/* Security Indicator */}
        {isSecure && (
          <Badge theme={BADGE_THEMES.secure} text="Secure" size="xs" />
        )}

        {/* Opt-out Status */}
        {question.hasOptOut && question.optedOut && (
          <Badge theme={BADGE_THEMES.opted_out} text="Opted Out" size="xs" />
        )}

        {/* Copy values (visible only when NOT editing) */}
        {!isEditMode && (
          <div className="ml-2 relative">
            <button
              type="button"
              onClick={handleCopyAll}
              className="inline-flex items-center p-2 rounded border text-xs font-medium bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              aria-label="Copy values of this question"
              title="Copy values"
              disabled={isAddTaxFileHistoryLoading}
            >
              {isAddTaxFileHistoryLoading ? (
                <Loading size="small" />
              ) : (
                <svg
                  className="w-3.5 h-3"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 23 23"
                  fill="currentColor"
                >
                  <path d="M7 4V2H17V4H20.0066C20.5552 4 21 4.44495 21 4.9934V21.0066C21 21.5552 20.5551 22 20.0066 22H3.9934C3.44476 22 3 21.5551 3 21.066V4.9934C3 4.44476 3.44495 4 3.9934 4H7ZM7 6H5V20H19V6H17V8H7V6ZM9 4V6H15V4H9Z"></path>
                </svg>
              )}
            </button>

            {/* Feedback bubble */}
            {copyStatus && (
              <div className="absolute right-0 mt-1 px-2 py-1 text-[10px] rounded bg-gray-800 text-white whitespace-nowrap shadow-sm">
                {copyStatus === 'copied' &&
                  `Copied ${copiedCount} value${copiedCount > 1 ? 's' : ''}`}
                {copyStatus === 'empty' && 'No values to copy'}
                {copyStatus === 'error' && 'Copy failed'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

QuestionHeader.propTypes = {
  question: PropTypes.shape({
    questionId: PropTypes.string,
    questionNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    questionText: PropTypes.string,
    required: PropTypes.bool,
    isRequired: PropTypes.bool,
    secure: PropTypes.bool,
    isSecure: PropTypes.bool,
    optedOut: PropTypes.bool,
    hasOptOut: PropTypes.bool,
    unanswered: PropTypes.bool,
    answer: PropTypes.any,
    validationErrors: PropTypes.array,
    isValid: PropTypes.bool,
    adminModified: PropTypes.bool,
    questionType: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  hasError: PropTypes.bool,
};

QuestionHeader.defaultProps = {
  hasError: undefined,
};

export default memo(QuestionHeader);
